import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { RefreshCw, HeartPulse } from 'lucide-react'
import { ServerApiClient, type FounderSystemHealthResponse, type FounderSystemHealthSeries } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

type Props = {
  topInsetPx?: number
}

const panel: CSSProperties = {
  border: '1px solid #d9e2df',
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}
const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  background: '#fff',
  color: '#15343b',
  borderRadius: 7,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}
const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  color: '#5d6d72',
  fontWeight: 800,
  borderBottom: '1px solid #e4ece9',
  padding: '8px 10px',
  whiteSpace: 'nowrap',
}
const tdStyle: CSSProperties = {
  fontSize: 12,
  color: '#223236',
  borderBottom: '1px solid #eef3f1',
  padding: '8px 10px',
  verticalAlign: 'top',
}
const numTd: CSSProperties = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const fmtNum = (v: number | null | undefined, digits = 0): string =>
  v == null || !Number.isFinite(Number(v)) ? '-' : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits })
const fmtAge = (secs: number | null | undefined): string => {
  if (secs == null || !Number.isFinite(Number(secs))) return '-'
  const s = Math.max(0, Math.round(Number(secs)))
  if (s >= 3600) return `${(s / 3600).toFixed(1)} h`
  if (s >= 60) return `${Math.round(s / 60)} min`
  return `${s} s`
}

const alarmColors = (state: string): { bg: string; border: string; text: string } => {
  const s = state.toUpperCase()
  if (s === 'ALARM') return { bg: '#fdeeee', border: '#e5b4b4', text: '#8c2f2f' }
  if (s === 'OK') return { bg: '#eef6f1', border: '#bcd9c8', text: '#1e5c3a' }
  return { bg: '#f4f4ee', border: '#d8d8c8', text: '#6b6b52' }
}

function Sparkline({ series }: { series: FounderSystemHealthSeries }) {
  const points = series.points
  const path = useMemo(() => {
    if (!points.length) return ''
    const vs = points.map((p) => p.v)
    const min = Math.min(...vs, 0)
    const max = Math.max(...vs, 1)
    const span = max - min || 1
    const w = 260
    const h = 48
    return points
      .map((p, i) => {
        const x = (i / Math.max(1, points.length - 1)) * w
        const y = h - ((p.v - min) / span) * (h - 4) - 2
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
  }, [points])
  const latest = points.length ? points[points.length - 1].v : null
  const peak = points.length ? Math.max(...points.map((p) => p.v)) : null
  return (
    <div style={{ ...panel, padding: 12, display: 'grid', gap: 6, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#657478', fontWeight: 800 }}>{series.label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 20, color: '#172d33', fontWeight: 850, lineHeight: 1 }}>{fmtNum(latest, 1)}</span>
        <span style={{ fontSize: 11, color: '#7b898d' }}>24h peak {fmtNum(peak, 1)}</span>
      </div>
      <svg width="100%" height={48} viewBox="0 0 260 48" preserveAspectRatio="none" role="img" aria-label={`${series.label} over the last 24 hours`}>
        {path ? <path d={path} fill="none" stroke="#2d6a67" strokeWidth={1.6} /> : null}
      </svg>
    </div>
  )
}

/** Founder System health tab: alarm states, 24h scaling metrics, queue backlog, in-process caches. */
export function FounderSystemHealthDashboard({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const loadSeqRef = useRef(0)
  const didInitialLoadRef = useRef(false)
  const [data, setData] = useState<FounderSystemHealthResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    const requestId = loadSeqRef.current + 1
    loadSeqRef.current = requestId
    setLoading(true)
    setError('')
    try {
      const session = await ensureApiSessionReady()
      if (requestId !== loadSeqRef.current) return
      if (!session.token) {
        const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
        window.history.replaceState(window.history.state, '', `/login?next=${encodeURIComponent(next)}`)
        window.dispatchEvent(new Event('fg_route_change'))
        return
      }
      const out = await api.getFounderSystemHealth()
      if (requestId !== loadSeqRef.current) return
      setData(out)
    } catch (e) {
      if (requestId !== loadSeqRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load system health')
    } finally {
      if (requestId === loadSeqRef.current) setLoading(false)
    }
  }, [api])

  useEffect(() => {
    if (didInitialLoadRef.current) return
    didInitialLoadRef.current = true
    void load()
  }, [load])

  // Passive refresh once a minute while the tab is open.
  useEffect(() => {
    const handle = window.setInterval(() => void load(), 60_000)
    return () => window.clearInterval(handle)
  }, [load])

  const alarmsInAlarm = (data?.alarms || []).filter((a) => a.state.toUpperCase() === 'ALARM')
  const sortedAlarms = [...(data?.alarms || [])].sort((a, b) => {
    const rank = (s: string) => (s.toUpperCase() === 'ALARM' ? 0 : s.toUpperCase() === 'INSUFFICIENT_DATA' ? 1 : 2)
    return rank(a.state) - rank(b.state) || a.name.localeCompare(b.name)
  })

  return (
    <div style={{ padding: 16, paddingTop: topInsetPx + 16, display: 'grid', gap: 14, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <HeartPulse size={16} color="#15343b" />
        <strong style={{ fontSize: 14, color: '#15343b' }}>System health</strong>
        {data ? (
          <span style={{ fontSize: 11, color: '#7b898d' }}>
            as of {fmtDate(data.generatedAt)} · api RSS {data.process.rssMb != null ? `${fmtNum(data.process.rssMb)} MB` : 'n/a'}
          </span>
        ) : null}
        <span style={{ flex: 1 }} />
        <button style={buttonStyle} onClick={() => void load()} disabled={loading}>
          <RefreshCw size={13} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error ? (
        <div style={{ ...panel, padding: 12, borderColor: '#e5b4b4', background: '#fdeeee', color: '#8c2f2f', fontSize: 12 }}>{error}</div>
      ) : null}
      {(data?.awsErrors || []).map((msg, i) => (
        <div key={i} style={{ ...panel, padding: 10, borderColor: '#e0d3ae', background: '#faf5e4', color: '#6d5a1d', fontSize: 11 }}>
          Partial data — {msg}
        </div>
      ))}

      {/* Alarm states */}
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b' }}>
          Alarms {data ? `(${alarmsInAlarm.length} firing / ${data.alarms.length})` : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
          {sortedAlarms.map((alarm) => {
            const colors = alarmColors(alarm.state)
            return (
              <div key={alarm.name} style={{ ...panel, padding: 10, background: colors.bg, borderColor: colors.border, display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 900, color: colors.text, letterSpacing: 0.4 }}>{alarm.state.toUpperCase()}</span>
                  <span style={{ fontSize: 10, color: '#7b898d', marginLeft: 'auto' }}>{fmtDate(alarm.updatedAt)}</span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 750, color: '#223236', overflowWrap: 'anywhere' }}>{alarm.name}</div>
                {alarm.state.toUpperCase() === 'ALARM' && alarm.reason ? (
                  <div style={{ fontSize: 11, color: colors.text }}>{alarm.reason}</div>
                ) : null}
              </div>
            )
          })}
          {data && !data.alarms.length ? (
            <div style={{ ...panel, padding: 12, fontSize: 12, color: '#7b898d' }}>No alarms found — has Terraform been applied?</div>
          ) : null}
        </div>
      </div>

      {/* 24h metric sparklines */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
        {(data?.series || []).map((series) => (
          <Sparkline key={series.id} series={series} />
        ))}
      </div>

      {/* Queues */}
      <div style={{ ...panel, overflow: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b', padding: '10px 10px 0' }}>Work queues</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Queue</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Visible messages</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Oldest message</th>
            </tr>
          </thead>
          <tbody>
            {(data?.queues || []).map((queue) => (
              <tr key={queue.name}>
                <td style={tdStyle}>{queue.name}</td>
                <td style={numTd}>{fmtNum(queue.visibleMessages)}</td>
                <td style={numTd}>{fmtAge(queue.oldestMessageAgeSecs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* In-process caches + render concurrency */}
      <div style={{ ...panel, overflow: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b', padding: '10px 10px 0' }}>
          API in-process caches
          {data ? (
            <span style={{ fontWeight: 600, color: '#7b898d' }}>
              {' '}
              · parquet materializations in flight {data.process.parquetMaterializeInUse}/{data.process.parquetMaterializeMax}
            </span>
          ) : null}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Cache</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Entries</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cap</th>
            </tr>
          </thead>
          <tbody>
            {(data?.process.cacheStats || []).map((stat) => (
              <tr key={stat.name}>
                <td style={tdStyle}>{stat.name}</td>
                <td style={numTd}>{fmtNum(stat.entries)}</td>
                <td style={numTd}>{fmtNum(stat.maxEntries)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
